import { REQUEST_METHOD } from '@/constants';
import { apiInstance } from '@/utils';
import type { AccountPriority } from '@/types/prospects';

export interface OooHoldStatus {
  source_history_id: number;
  recipient_email: string;
  resume_at: string;
  scheduled_at: string;
  audience_id: number;
  audience_name: string;
  owner_id: string;
  owner_name: string;
  reason: 'out_of_office';
  status: 'paused' | 'resume_pending' | 'resumed';
  resumed_at: string | null;
}

export const getOooHoldStatuses = async (vendorId: number, assignedTo: string) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: '/private/gtm-tasks/ooo-holds',
    params: { vendorId, assignedTo },
  });
  return result.data as { success: boolean; data: { holds: OooHoldStatus[]; has_more: boolean } };
};

/**
 * GTM Signals Service
 * API calls for the GTM Signal Engine / Tasks page
 */

// Types

/** Latest synced, non-deleted deal for this workspace and company. */
export interface CrmDealHistory {
  id: string;
  crm_type: string;
  deal_name: string | null;
  stage_name: string | null;
  is_closed_lost: boolean | null;
  is_closed_won: boolean | null;
  deal_closed_at: string | null;
  loss_reason: string | null;
  owner_name: string | null;
}


// Workflow status values (derived from play_content_drafts lifecycle + outreach_status)
export type WorkflowStatus = 'pending' | 'pending_review' | 'ready' | 'in_progress' | 'bounced' | 'stalled' | 'executed' | 'failed' | 'completed' | 'replied';

// Processing status values (from gtmSignalWorker)
type ProcessingStatus =
  | 'pending'
  | 'evaluating'
  | 'enriched'
  | 'evaluation_skipped'
  | 'persona_discovery'
  | 'personas_discovered'
  | 'needs_contact'
  | 'awaiting_acceptance'
  | 'completed'
  | 'failed'
  | 'pattern_exclusion'      // Excluded via global vendor patterns (before AI evaluation)
  | 'customer_exclusion'     // Existing customer (ignored_customers table)
  | 'signal_type_disabled'   // Signal type disabled by workspace admin
  | 'dismissed';             // User dismissed (stored in dismissed boolean)

// Workspace user for assignee dropdowns
export interface WorkspaceUser {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  profile_pic_url: string | null;
  signature: string | null;
  is_admin: boolean;
  /**
   * Workspace product experience, independent of authorization role. 'sdr' and
   * 'ae' members have their own Today dashboards; the endpoint already returns this.
   */
  ui_persona?: 'general' | 'sdr' | 'ae';
  /**
   * Holds at least one non-dismissed signal created in the last 30 days, i.e.
   * is actually working the Actions queue. Keeps an unmarked but active
   * assignee in the Tasks SDR pickers — see views/tasks/utils/sdrRoster.
   */
  has_active_signals?: boolean;
  /** Owns at least one open CRM opportunity in this workspace. */
  owns_open_crm_deals?: boolean;
}

// CRM Lookup Data - stores whether company/contact exists in CRM
export interface CrmLookupData {
  hubspot?: {
    // Company data
    company_exists?: boolean;
    company_id?: string;
    company_url?: string;
    company_name?: string;
    // Company enrichment (populated by signal-level domain lookup)
    industry?: string;
    employee_count?: string;
    city?: string;
    state?: string;
    country?: string;
    annual_revenue?: string;
    description?: string;
    company_type?: string;
    // Contact data
    contact_exists?: boolean;
    contact_id?: string;
    contact_url?: string;
    lifecycle_stage?: string;
    last_activity?: string;
    owner_id?: string;
    owner_name?: string;
    owner_email?: string;
    last_checked?: string;
  };
  salesforce?: {
    // Account data
    account_exists?: boolean;
    account_id?: string;
    account_url?: string;
    account_name?: string;
    // Account enrichment (populated by signal-level domain lookup)
    industry?: string;
    employee_count?: string;
    city?: string;
    state?: string;
    country?: string;
    annual_revenue?: string;
    description?: string;
    company_type?: string;
    // Contact data
    contact_exists?: boolean;
    contact_id?: string;
    contact_url?: string;
    owner_name?: string;
    owner_email?: string;
    last_activity_date?: string;
    last_checked?: string;
  };
}

export interface MbOpportunityBadgeData {
  exists: boolean;
  opportunity_id: string | null;
  deal_name: string | null;
  amount_cents: number | null;
  stage_normalized: string | null;
  is_closed: boolean;
}

/** mb-server#5355: written by the inbound-response path (HubSpot hook / Salesforce poller). */
export interface GtmSignalInboundPayload {
  provider: 'hubspot' | 'salesforce';
  objectType: string;
  objectId: string;
  occurredAt: string | null;
  sourceName: string | null;
  conversionName?: string | null;
  matchedFields?: string[];
  recordUrl?: string | null;
  pageUrl?: string | null;
  ledgerId?: number | null;
  /** What the person told us on the form, per the policy's request_details. */
  details?: Array<{ field?: string; label: string; value: string }>;
}

interface GtmSignalPayload {
  /** Present only on tasks the inbound policy created. */
  inbound?: GtmSignalInboundPayload;
  /** The policy's default response campaign; a recommendation, not a lock. */
  recommendedCampaignAudienceId?: number | null;
  /** 'nurture_hot_account' on a card the nurture loop put in Actions when the account heated up. */
  promotionOrigin?: string;
  linkedinUrl?: string;
  industry?: string;
  companySize?: string;
  location?: string;
  country?: string;
  geo?: {
    country?: string;
  };
  locationData?: {
    country?: string;
  };
  personData?: {
    linkedinUrl?: string;
    phone?: string;
  };
  [key: string]: unknown;
}

interface GtmSignalEnrichmentData {
  phone?: string;
  industry?: string;
  employee_count?: string | number;
  employeeRange?: string;
  revenue?: string | number;
  description?: string;
  location?: string;
  country?: string;
  headquarters_location?: string;  // AI-enriched (e.g., "Jakarta, Indonesia")
  region?: 'NA' | 'EMEA' | 'APAC' | 'LATAM' | 'Unknown';  // AI-enriched
  city?: string;
  geo?: {
    city?: string;
    state?: string;
    country?: string;
  };
  linkedinUrl?: string;
  linkedin_url?: string;
  ai_context?: Record<string, unknown> | null;
  [key: string]: unknown;
}

// Lightweight persona for list view (tree in left panel)
export interface PersonaEmail {
  id: number;
  type: 'sent' | 'reply';
  subject: string | null;
  body?: string | null;  // For sent emails
  body_html?: string | null;  // For replies
  body_text?: string | null;  // For replies
  cc?: string | null;  // CC recipients (for inbound replies)
  thread_id: string | null;
  message_id: string | null;
  timestamp: string;
  direction: 'outbound' | 'inbound';
  from_email?: string | null;  // Sender mailbox email (for sent emails)
  from_name?: string | null;   // Display name parsed from the source message headers
  to_email?: string | null;    // Actual recipient (sent emails in redirected threads may differ from persona.email)
  /**
   * The underlying `email_send_history.status` for an outbound row, when the source supplies it
   * (today: GET /private/analytics/contact-emails). `'sent'` is a real delivery; `'test_run'` is a
   * test-mode preview that was built and never sent. An outbound row is NOT proof of a delivery —
   * see mb-frontend#4341. Undefined where the source does not carry it; null on inbound replies.
   */
  send_status?: string | null;
  metadata?: Record<string, any>;  // AI classification, etc.
}

export interface GtmSignalPersonaSummary {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;  // job title
  linkedin_url: string | null;
  enrichment_status: 'provided' | 'enriched' | 'found' | null;
  enrichment_source: string | null;
  email_verified: boolean;
  email_validation_status: string | null;  // 'valid', 'catch-all', 'invalid', 'unknown', 'verified', 'validation_error', 'do_not_mail', or null
  email_finding_status: string | null;  // 'api_provided', 'pattern_generated', 'ai_discovered', 'manual_edit', or null
  // GH#5175: consent, not deliverability — a MarketBetter unsubscribe or a CRM opt-out for a
  // workspace armed with CRM enforcement. `email_validation_status` answers whether the address
  // works; this answers whether we are allowed to write to it at all.
  email_suppressed?: boolean | null;
  matched_persona_title: string | null;  // AI-recommended persona category (e.g., "Marketing Leader")
  matched_persona_reason: string | null;  // Why this person matches (e.g., "Oversees marketing strategy...")
  buying_committee_role: 'champion' | 'decision_maker' | 'evaluator' | 'blocker' | null;
  outreach_angle: string | null;
  signal_relevance: string | null;
  confidence_score: number | null;  // AI confidence score (0-100)
  crm_lookup_data?: CrmLookupData;  // CRM status (exists in HubSpot/Salesforce)
  mb_opportunity?: MbOpportunityBadgeData | null;
  workflow_status: WorkflowStatus;  // Derived: pending → pending_review → ready → in_progress → executed
  bounced_email?: string | null;  // Recipient address from the latest bounce in the current run
  locked_workflow_id: number | null;  // ID of the active workflow (queried via people.dynamic_col_1 linkage)
  locked_workflow_name: string | null;  // Name of the active workflow
  ai_sdr_sent_at?: string | null;  // Set when AI SDR auto-sent this persona's outreach (no human review)
  // #3188: set when a HubSpot contact matching this persona's email has last_activity > signal.created_at
  externally_contacted_at?: string | null;
  externally_contacted_source?: string | null;
  // #3188: authoritative "who/where" for the badge, sourced directly from hubspot_contacts (signal detail)
  externally_contacted_owner_name?: string | null;
  externally_contacted_contact_url?: string | null;
  original_index?: number;  // Original position in API response before sorting (for ai_recommended_personas mapping)
  emails?: PersonaEmail[];  // Email thread for email_reply signals
}

export interface GtmSignal {
  id: string;  // UUID
  vendor_id: number;
  signal_type: string;
  signal_source: string;
  company_domain: string | null;
  company_name: string | null;
  person_email: string | null;
  // Person data extracted from signal_payload (for person-centric list view)
  person_first_name?: string | null;
  person_last_name?: string | null;
  person_location?: string | null;
  signal_payload?: GtmSignalPayload;  // Raw signal data (location, companySize, linkedinUrl, etc.)
  /** Automated inbound message that named this signal's referral target. */
  referral_source_email?: PersonaEmail | null;
  enrichment_data: GtmSignalEnrichmentData;
  ai_evaluation_status: string;
  ai_fit_label: string | null;
  ai_fit_score: number | null;
  account_priority?: AccountPriority | null;
  account_priority_enabled?: boolean;
  account_priority_available?: boolean;
  outreach_eligibility?: NonNullable<AccountPriority['routing']>['outreachEligibility'];
  ai_fit_reasoning: string | string[] | null;  // Can be string (legacy) or array of strings (new format)
  ai_recommended_personas: Array<{
    title: string;
    reason: string;
  }>;
  processing_status: ProcessingStatus;
  created_at: string;
  updated_at: string;
  company_crm_data?: CrmLookupData;  // CRM status for the company
  mb_opportunity?: MbOpportunityBadgeData | null;
  // Lightweight persona summary (list view optimization - replaces full personas array)
  persona_count: number;  // Count of actionable personas (with email or phone)
  has_replied: boolean;  // True if any persona has replied (email or LinkedIn)
  has_completed: boolean;  // True if any persona's workflow completed
  has_in_progress: boolean;  // True if any persona has active workflow
  // Legacy fields for backwards compatibility (detail view still returns full personas)
  discovered_persona_count?: number;  // @deprecated - use persona_count
  personas?: GtmSignalPersonaSummary[];  // Only populated in detail view, optional in list
  assigned_to: string | null;  // User UUID
  assigned_at: string | null;
  assigned_by: string | null;
  assignee: WorkspaceUser | null;  // Full assignee details
  dismissed?: boolean;  // Whether signal has been dismissed
  dismissed_at?: string | null;  // Timestamp when dismissed
  dismissal_reason?: string | null;  // Reason for dismissal
  latest_crm_deal?: CrmDealHistory | null;
  has_closed_lost_history?: boolean;  // True if company has closed/lost deals (GH#799)
  /**
   * The shared customer relationship (mb-server#6451), same four fields and same
   * definition the list rows carry — the detail panel is where a rep decides
   * whether to pitch, so the badge and its reason have to survive the drill-in.
   */
  is_customer?: boolean;
  customer_source?: 'manual' | 'customer_rule' | 'closed_won' | 'crm_contact' | null;
  customer_reason?: string | null;
  customer_matched_domain?: string | null;
}

// Full persona detail for right panel
export interface GtmSignalPersona {
  id: string;
  signal_id: string;
  persona_title: string;  // matched_persona_title - the ICP persona they matched
  person_name: string | null;  // CONCAT(first_name, last_name)
  first_name: string | null;
  last_name: string | null;
  person_email: string | null;  // email
  person_linkedin: string | null;  // linkedin_url
  job_title: string | null;  // their actual job title
  phone: string | null;
  seniority: string | null;
  enrichment_source: string | null;
  enrichment_status: string | null;
  email_verified: boolean;
  email_validation_status: string | null;  // 'valid', 'catch-all', 'invalid', 'unknown', 'verified', 'validation_error', 'do_not_mail', or null
  email_finding_status: string | null;  // 'api_provided', 'pattern_generated', 'ai_discovered', 'manual_edit', or null
  // GH#5175: consent, not deliverability — a MarketBetter unsubscribe or a CRM opt-out for a
  // workspace armed with CRM enforcement. `email_validation_status` answers whether the address
  // works; this answers whether we are allowed to write to it at all.
  email_suppressed?: boolean | null;
  confidence_score: number | null;
  buying_committee_role: 'champion' | 'decision_maker' | 'evaluator' | 'blocker' | null;
  outreach_angle: string | null;
  signal_relevance: string | null;
  crm_lookup_data?: CrmLookupData;  // CRM status (exists in HubSpot/Salesforce)
  mb_opportunity?: MbOpportunityBadgeData | null;
  workflow_status: WorkflowStatus;  // Derived: pending → pending_review → ready → in_progress → executed
  bounced_email?: string | null;  // Recipient address from the latest bounce in the current run
  locked_workflow_id: number | null;  // ID of the active workflow (from audiences table)
  locked_workflow_name: string | null;  // Name of the active workflow
  ai_sdr_sent_at?: string | null;  // Set when AI SDR auto-sent this persona's outreach (no human review)
  created_at: string;
  updated_at: string;
  emails?: PersonaEmail[];  // Email thread for email_reply signals
}

export interface GtmSignalWithPersonas extends Omit<GtmSignal, 'personas'> {
  signal_payload: GtmSignalPayload;
  ai_evaluated_at: string | null;
  ai_evaluation_model: string | null;
  personas: GtmSignalPersona[];  // Full persona details for right panel
  dismissed?: boolean;  // Whether signal has been dismissed
  dismissed_at?: string | null;  // Timestamp when dismissed
  dismissal_reason?: string | null;  // Reason for dismissal
  latest_crm_deal?: CrmDealHistory | null;
  has_closed_lost_history?: boolean;  // True if company has closed/lost deals (GH#799)
}

// Persona-centric list item for Tasks page
// Each row = one persona with signal context (no company-name fallbacks)
export interface PersonaListItem {
  /** Signal-only rows have no backing gtm_signal_personas id or persona actions. */
  row_kind?: 'persona' | 'signal';
  persona_id: string;
  first_name: string | null;
  last_name: string | null;
  persona_email: string | null;
  persona_phone: string | null;
  persona_title: string | null;
  persona_linkedin_url: string | null;
  /**
   * The person's LinkedIn photo from enrichment. Null for most rows — the cache is
   * only populated where profile enrichment has run — so the company mark remains
   * the common case, not the exception.
   */
  persona_photo_url?: string | null;
  enrichment_status: string | null;
  enrichment_source: string | null;
  confidence_score: number | null;
  email_verified: boolean;
  email_validation_status: string | null;
  email_finding_status: string | null;
  // GH#5175: consent, not deliverability — a MarketBetter unsubscribe or a CRM opt-out for a
  // workspace armed with CRM enforcement. `email_validation_status` answers whether the address
  // works; this answers whether we are allowed to write to it at all.
  email_suppressed?: boolean | null;
  matched_persona_title: string | null;
  buying_committee_role: 'champion' | 'decision_maker' | 'evaluator' | 'blocker' | null;
  outreach_angle: string | null;
  crm_lookup_data?: CrmLookupData;
  mb_opportunity?: MbOpportunityBadgeData | null;
  persona_workflow_status: 'new' | 'in_progress' | 'bounced' | 'completed';
  bounced_email?: string | null;
  locked_workflow_id: number | null;
  locked_workflow_name: string | null;
  ai_sdr_sent_at?: string | null; // Set when AI SDR auto-sent this persona's outreach (no human review)
  // Signal context
  signal_id: string;
  signal_type: string;
  /**
   * A one-tap enrolment from /signals creates a manual_promotion signal; this is the activity the
   * rep enrolled from (website_visit, ...), which Actions shows instead (mb-server#6116).
   */
  origin_signal_type?: string | null;
  signal_source: string;
  /** The listed contact was named by someone else's automated reply. */
  is_referral_target?: boolean;
  /** Sender of the automated message that named the listed contact. */
  referral_source_email?: string | null;
  user_note?: string | null;
  company_domain: string | null;
  company_name: string | null;
  enrichment_data: GtmSignalEnrichmentData;
  ai_evaluation_status: string;
  ai_fit_label: string | null;
  ai_fit_score: number | null;
  account_priority?: AccountPriority | null;
  account_priority_enabled?: boolean;
  account_priority_available?: boolean;
  outreach_eligibility?: NonNullable<AccountPriority['routing']>['outreachEligibility'];
  ai_fit_reasoning: string | string[] | null;
  processing_status: string;
  signal_payload?: Record<string, unknown>;
  signal_created_at: string;
  workflow_started_at?: string | null; // When workflow was triggered (audiences.created_at)
  // The Enrolled lane's row line (mb-server#6100, mb-frontend#4913). Present only on the Enrolled
  // lane and a signal-scoped request; computed server-side off the person's own campaign so the
  // row, the panel and the sort cannot each answer "when does the next one go out" differently.
  // `starts_at` is set while nothing has sent, `next_send_at` once something has — never both.
  starts_at?: string | null;
  next_send_at?: string | null;
  campaign_never_launched?: boolean;
  last_sent_at?: string | null;
  emails_sent_count?: number;
  email_step_count?: number;
  linkedin_step_count?: number;
  next_step?: { channel: 'email' | 'linkedin'; type: string | null; sequenceOrder: number } | null;
  /** Why this person's sequence cannot move, when it cannot: the outcome the executor recorded. */
  enrollment_outcome?: 'pending' | 'enrolled' | 'skipped_no_contact' | 'skipped_suppressed'
    | 'waiting_for_sender' | 'no_sending_channel' | 'canceled' | null;
  /** The CRM that already contacted them, and when — `hubspot` or `salesforce` on production. */
  externally_contacted_at?: string | null;
  externally_contacted_source?: string | null;
  /** A step of their own campaign is held at 'paused' (mb-server#6125). Enrolled lane only. */
  sequence_paused?: boolean;
  /** A rep stopped the sequence and it has not been started again. Enrolled lane only. */
  sequence_stopped?: boolean;
  /** Who pressed Enroll — not always the account's owner (acting for a rep). Enrolled lane only. */
  enrolled_by_name?: string | null;
  time_bucket: 'today' | 'this_week' | 'older';
  // Assignment (signal-level)
  assigned_to: string | null;
  assigned_at: string | null;
  assigned_by: string | null;
  assignee: WorkspaceUser | null;
  // Dismissal (signal-level)
  dismissed?: boolean;
  dismissed_at?: string | null;
  dismissal_reason?: string | null;
  // Dismissal (persona-level, GH#760)
  persona_dismissed?: boolean;
  persona_dismissed_at?: string | null;
  persona_dismissal_reason?: string | null;
  // Closed/lost history (GH#799)
  latest_crm_deal?: CrmDealHistory | null;
  has_closed_lost_history?: boolean;
  // CRM exclusion flags — company is a known customer or open opportunity
  is_customer?: boolean;
  is_opportunity?: boolean;
  // Why the server calls this company a customer (mb-server#6451), from the one
  // definition Signals, the audience builder and the send-time skip all read.
  // `customer_matched_domain` can be an ancestor of `company_domain` — The Good
  // Guys' signals arrive on thegoodguys.com.au while the mark sits on the brand
  // domain — and the badge's tooltip says so, because otherwise the badge looks
  // wrong to the one person who knows the difference.
  customer_source?: 'manual' | 'customer_rule' | 'closed_won' | 'crm_contact' | null;
  customer_reason?: string | null;
  customer_matched_domain?: string | null;
  // AI-classified sentiment of the prospect's inbound reply ('positive' |
  // 'negative' | 'neutral' | 'automated'). Populated for reply rows so the
  // Tasks list shows a sentiment badge instead of the generic "Email Reply"
  // signal-type label. Null when not a reply or unclassified. Populated for
  // email AND LinkedIn replies (#3728).
  reply_classification?: string | null;
  // Replies tab only. 'answered' = the rep sent the last word on an email reply and the person is
  // kept in the lane as a conversation; 'needs_reply' = the prospect wrote again after the answer.
  // Null for every row that is not an answered email reply.
  conversation_state?: 'answered' | 'needs_reply' | null;
  // When the rep answered the email reply (outreach completed). Null when never answered.
  reply_answered_at?: string | null;
  // One-line AI analysis of the reply (LinkedIn replies, #3728) — surfaced as
  // hover context on the sentiment badge so an SDR sees WHY.
  reply_analysis?: string | null;
  reply_snippet?: string | null;
  // True when the prospect explicitly asked to stop being messaged (#3728) —
  // an opt-out is a different triage action than a generic "not interested".
  reply_unsubscribe?: boolean;
  // Channel of the classified reply ('linkedin' | 'email') — drives the small
  // channel chip beside the sentiment label so LinkedIn and email replies are
  // distinguishable at a glance in the list. Null when there's no classification.
  reply_channel?: 'linkedin' | 'email' | null;
  // mb-server#5355: inbound-response lifecycle, shared by Actions and Today (mb-frontend#4459).
  // Null for every task the inbound policy did not create.
  recommended_campaign_audience_id?: number | null;
  inbound?: {
    provider: 'hubspot' | 'salesforce';
    source_name: string | null;
    occurred_at: string | null;
    record_url: string | null;
    ledger_status: string | null;
    owner_resolution: string | null;
    readiness: { status: 'ready' | 'meeting_booked' | 'setup_required' | 'needs_owner'; reasons: Array<{ code: string; severity: string; message: string; action?: string | null }>; computedAt?: string } | null;
    drafts_ready_at: string | null;
    drafts_error_code: string | null;
    has_drafts: boolean;
  } | null;
}

/**
 * The three modes of the Customers filter (mb-server#6451). One union for every
 * surface that offers it — the Tasks list, its tab counts and the Signals Hub —
 * because a mode that reaches one of them and not the others is a badge count
 * that disagrees with the list it labels.
 */
export type CustomerFilterMode = 'all' | 'exclude' | 'only';

interface PersonaListParams {
  vendorId: number;
  page?: number;
  pageSize?: number;
  signalType?: string;
  fitLabels?: string[];
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  workflowStatusFilter?: 'all' | 'new' | 'replied' | 'inbound' | 'referrals' | 'follow_up' | 'enrolled' | 'in_progress' | 'needs_contact' | 'failed' | 'dismissed' | 'completed' | 'favorites';
  assignedTo?: 'unassigned' | 'me' | string;
  /** Admin Today drill-in; server re-validates admin + workspace + SDR persona. */
  todayViewAsUserId?: string;
  timeRange?: 'today' | 'month' | '24h' | '7d' | '30d' | '90d';
  createdFrom?: string;
  createdTo?: string;
  /** Inclusive lower bound on reply occurrence time, independent of signal age. */
  replyFrom?: string;
  /** Replies lane only: leave out conversations the rep has the last word on. */
  repliesTodoOnly?: boolean;
  includeCustomers?: boolean;
  /**
   * Existing-customer scope (mb-server#6451), the same definition the Customer
   * badge on the rows reads. Separate from `includeCustomers`, which is the older
   * combined customer/opportunity/ignored gate the Tasks page pins to `true` and
   * which still applies on top.
   */
  customerFilter?: CustomerFilterMode;
  includeSdrChatOutsideFitLabels?: boolean;
  replyChannel?: 'email' | 'linkedin';
  /** mb-server#5355 / #4459 — Today inbound section. */
  inboundOnly?: boolean;
  /**
   * One Action, named by its gtm_signals id (mb-frontend#4507). The server treats it as
   * authoritative and ignores every other filter, so the Action is returned whichever tab
   * it would normally sit under and whoever it belongs to.
   */
  signalId?: string;
}

type TaskTimeRange = NonNullable<PersonaListParams['timeRange']>;
type TaskQueryParams = Record<string, string | number | boolean>;

const applyTaskTimeRangeParams = (
  queryParams: TaskQueryParams,
  timeRange?: TaskTimeRange,
) => {
  if (timeRange === 'today' || timeRange === 'month') {
    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);
    start.setHours(0, 0, 0, 0);
    if (timeRange === 'month') start.setDate(1);
    end.setHours(23, 59, 59, 999);
    queryParams.createdFrom = start.toISOString();
    queryParams.createdTo = end.toISOString();
  } else if (timeRange) {
    queryParams.createdWithin = timeRange;
  }
};

/**
 * An account enrolled before anyone was found (mb-server#6118): the Enrolled lane returns these
 * beside the people, outside the page, until discovery claims a person onto the enrolment.
 */
export interface DiscoveringEnrollment {
  enrollment_id: string;
  signal_id: string;
  status: 'requested' | 'finding_people' | 'finding_contact' | 'ready' | 'waiting_for_sender' | 'no_contacts_found';
  status_reason: string | null;
  mode: 'company_led' | 'person_led';
  requested_at: string;
  discovery_started_at: string | null;
  discovery_completed_at: string | null;
  updated_at: string;
  enrolled_by: string | null;
  /** Who pressed Enroll, for "Enrolled by Dana" (mb-server, discoveringEnrollments). */
  enrolled_by_name?: string | null;
  company_name: string | null;
  company_domain: string | null;
  /** Person-led only: who it is about ("Vijay Verma"), since no search runs for them. */
  person_name?: string | null;
  person_title?: string | null;
  template_name: string | null;
}

export interface PersonaListResponse {
  success: boolean;
  data: {
    personas: PersonaListItem[];
    /** Enrolled lane, page 1 only. */
    discovering?: DiscoveringEnrollment[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  };
}

interface GtmSignalDetailResponse {
  success: boolean;
  data: GtmSignalWithPersonas;
}

// API Endpoints
const GTM_SIGNALS_ENDPOINTS = {
  personaList: '/private/analytics/gtm-signal-personas',
  // Signal-centric list. Used by the Needs Contact lane, whose rows have no
  // personas and so cannot come from personaList (mb-server#4424).
  list: '/private/analytics/gtm-signals',
  personaCounts: '/private/analytics/gtm-signal-persona-counts',
  detail: (signalId: string) => `/private/analytics/gtm-signals/${signalId}`,
  dismiss: (signalId: string) => `/private/analytics/gtm-signals/${signalId}/dismiss`,
  restore: (signalId: string) => `/private/analytics/gtm-signals/${signalId}/restore`,
  assign: (signalId: string) => `/private/analytics/gtm-signals/${signalId}/assign`,
  // Account-scoped, because nothing the Signals Hub shows is one signal: a row
  // aggregates every signal for the domain and `signal_summary` carries at most
  // one signal_id per type. See mb-server#5674.
  assignAccount: '/private/analytics/signals/account/assign',
  refreshAccountCrm: '/private/analytics/signals/account/refresh-crm',
  // Hand a contact to a contactless high-intent account. Creating the persona
  // is the whole handover — the Tasks lanes are persona-centric and the Slack
  // digest counts personas, so the account rejoins the normal flow by itself.
  addContact: (signalId: string) => `/private/analytics/gtm-signals/${signalId}/contact`,
  // Board 4's per-row "Add email or LinkedIn": fills in contact details on one persona the
  // direct path already skipped for lack of one. Distinct from addContact above, which creates
  // a brand new persona for a signal whose lookup found nobody at all.
  addPersonaContact: (signalId: string, personaId: string) => `/private/analytics/gtm-signals/${signalId}/personas/${personaId}/contact`,
  reviewQueue: '/private/analytics/today/review-queue',
  todayDismiss: '/private/analytics/today/dismiss',
  todayDismissUndo: '/private/analytics/today/dismiss/undo',
  acceptForOutreach: (signalId: string) => `/private/analytics/gtm-signals/${signalId}/accept-for-outreach`,
  undoAccept: (signalId: string) => `/private/analytics/gtm-signals/${signalId}/undo-accept`,
  // GH#760: Persona-level dismiss/restore
  personaDismiss: (personaId: string) => `/private/analytics/gtm-signals/personas/${personaId}/dismiss`,
  personaRestore: (personaId: string) => `/private/analytics/gtm-signals/personas/${personaId}/restore`,
  // #2136: Bulk persona dismiss
  personaBulkDismiss: '/private/analytics/gtm-signals/personas/bulk-dismiss',
  restartBouncedSequence: (personaId: string) => `/private/analytics/gtm-signals/personas/${personaId}/restart-bounced-sequence`,
};

const WORKSPACE_ENDPOINTS = {
  users: (workspaceId: number) => `/private/workspaces/${workspaceId}/users`,
};

/**
 * Get persona-centric list for Tasks page
 * Each row = one persona with signal context
 */
export const getGtmSignalPersonasList = async (
  params: PersonaListParams
): Promise<PersonaListResponse> => {
  const queryParams: Record<string, string | number | boolean> = {
    vendorId: params.vendorId,
    page: params.page || 1,
    pageSize: params.pageSize || 50,
  };

  if (params.signalType) queryParams.signalType = params.signalType;
  if (params.fitLabels && params.fitLabels.length > 0) {
    queryParams.fitLabels = params.fitLabels.join(',');
  }
  if (params.search) queryParams.search = params.search;
  if (params.sortBy) queryParams.sortBy = params.sortBy;
  if (params.sortDirection) queryParams.sortDirection = params.sortDirection;
  if (params.workflowStatusFilter && params.workflowStatusFilter !== 'all') {
    queryParams.workflowStatusFilter = params.workflowStatusFilter;
  }
  if (params.assignedTo) queryParams.assignedTo = params.assignedTo;
  if (params.todayViewAsUserId) queryParams.todayViewAsUserId = params.todayViewAsUserId;
  applyTaskTimeRangeParams(queryParams, params.timeRange);
  if (params.createdFrom) queryParams.createdFrom = params.createdFrom;
  if (params.createdTo) queryParams.createdTo = params.createdTo;
  if (params.replyFrom) queryParams.replyFrom = params.replyFrom;
  if (params.repliesTodoOnly) queryParams.repliesTodoOnly = 'true';
  if (params.includeCustomers) queryParams.includeCustomers = 'true';
  // Omitted on 'all' so the default never appears in the query string; the server
  // rejects anything outside the three modes rather than widening it.
  if (params.customerFilter && params.customerFilter !== 'all') queryParams.customerFilter = params.customerFilter;
  if (params.includeSdrChatOutsideFitLabels) queryParams.includeSdrChatOutsideFitLabels = 'true';
  if (params.replyChannel) queryParams.replyChannel = params.replyChannel;
  if (params.inboundOnly) queryParams.inboundOnly = true;
  if (params.signalId) queryParams.signalId = params.signalId;

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params: queryParams,
    url: GTM_SIGNALS_ENDPOINTS.personaList,
  });

  return result.data;
};

export interface PersonaCountsResponse {
  success: boolean;
  data: {
    new: number;
    replied: number;
    inbound: number;
    // Referral hand-offs nobody has contacted yet (mb-server#5475). Leaves the
    // bucket as soon as outreach is sent, like a worked reply leaves Replies.
    referrals: number;
    follow_up: number;
    /** The Enrolled lane. `in_progress` carries the same number under the name it shipped with. */
    enrolled: number;
    in_progress: number;
    completed: number;
    dismissed: number;
    social: number;
    // Counted per SIGNAL, not per persona — a needs_contact account has no
    // personas by definition (mb-server#4424).
    needs_contact: number;
  };
}

/**
 * Needs Contact list.
 *
 * Deliberately hits the SIGNAL-centric endpoint rather than the persona-centric
 * one every other tab uses. The Tasks Hub list is
 * `FROM gtm_signal_personas INNER JOIN gtm_signals`, and a needs_contact signal
 * has zero personas by definition — so no filter on that endpoint could ever
 * return these rows. The unit here is an account with nobody at it, not a
 * person, so the signal endpoint is also the semantically correct source.
 *
 * Rows are mapped onto PersonaListItem with null persona fields so the existing
 * table renders them without a parallel row type.
 */
export const getNeedsContactAccountsList = async (
  params: PersonaListParams
): Promise<PersonaListResponse> => {
  const queryParams: Record<string, string | number | boolean> = {
    vendorId: params.vendorId,
    page: params.page || 1,
    pageSize: params.pageSize || 50,
    workflowStatusFilter: 'needs_contact',
  };

  if (params.signalType) queryParams.signalType = params.signalType;
  if (params.fitLabels && params.fitLabels.length > 0) {
    queryParams.fitLabels = params.fitLabels.join(',');
  }
  if (params.search) queryParams.search = params.search;
  if (params.sortBy) queryParams.sortBy = params.sortBy;
  if (params.sortDirection) queryParams.sortDirection = params.sortDirection;
  if (params.assignedTo) queryParams.assignedTo = params.assignedTo;
  applyTaskTimeRangeParams(queryParams, params.timeRange);
  if (params.includeCustomers) queryParams.includeCustomers = 'true';
  if (params.customerFilter && params.customerFilter !== 'all') queryParams.customerFilter = params.customerFilter;

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params: queryParams,
    url: GTM_SIGNALS_ENDPOINTS.list,
  });

  const signals = (result.data?.data?.signals || []) as Array<Record<string, unknown>>;

  return {
    success: result.data?.success ?? true,
    data: {
      personas: signals.map(mapNeedsContactSignalToListItem),
      pagination: result.data?.data?.pagination ?? {
        page: params.page || 1,
        pageSize: params.pageSize || 50,
        total: 0,
        totalPages: 0,
      },
    },
  };
};

/** Map a signal-only Needs Contact account into the shared list-row shape. */
export const mapNeedsContactSignalToListItem = (signal: Record<string, unknown>): PersonaListItem => ({
  row_kind: 'signal',
  // No persona exists. persona_id is keyed off the signal so React keys stay
  // stable; persona fields are null so contact UI renders its missing state.
  persona_id: `needs-contact:${signal.id as string}`,
  first_name: null,
  last_name: null,
  persona_email: null,
  persona_phone: null,
  persona_title: null,
  persona_linkedin_url: null,
  enrichment_status: null,
  enrichment_source: null,
  confidence_score: null,
  email_verified: false,
  email_validation_status: null,
  email_finding_status: null,
  email_suppressed: false,
  matched_persona_title: null,
  buying_committee_role: null,
  outreach_angle: null,
  persona_workflow_status: 'new',
  locked_workflow_id: null,
  locked_workflow_name: null,
  signal_id: signal.id as string,
  signal_type: signal.signal_type as string,
  signal_source: signal.signal_source as string,
  user_note: ((signal.signal_payload as Record<string, unknown> | undefined)?.sdRNote as string) ?? null,
  company_domain: (signal.company_domain as string) ?? null,
  company_name: (signal.company_name as string) ?? null,
  enrichment_data: (signal.enrichment_data || {}) as GtmSignalEnrichmentData,
  ai_evaluation_status: signal.ai_evaluation_status as string,
  ai_fit_label: (signal.ai_fit_label as string) ?? null,
  ai_fit_score: (signal.ai_fit_score as number) ?? null,
  account_priority: (signal.account_priority as AccountPriority) ?? null,
  account_priority_enabled: (signal.account_priority_enabled as boolean) ?? false,
  account_priority_available: (signal.account_priority_available as boolean) ?? false,
  outreach_eligibility: (signal.outreach_eligibility as PersonaListItem['outreach_eligibility']) ?? 'unknown',
  ai_fit_reasoning: (signal.ai_fit_reasoning as string) ?? null,
  processing_status: signal.processing_status as string,
  signal_payload: signal.signal_payload as Record<string, unknown> | undefined,
  signal_created_at: signal.created_at as string,
  time_bucket: 'today',
  assigned_to: (signal.assigned_to as string) ?? null,
  assigned_at: (signal.assigned_at as string) ?? null,
  assigned_by: (signal.assigned_by as string) ?? null,
  assignee: (signal.assignee as WorkspaceUser) ?? null,
  dismissed: (signal.dismissed as boolean) ?? false,
  // Needs Contact rows are the ones that most need this badge: a contactless
  // account IS a company and nothing else, so "already a customer" is the whole
  // triage decision. The old inline chip never appeared on them at all.
  is_customer: (signal.is_customer as boolean) ?? false,
  customer_source: (signal.customer_source as PersonaListItem['customer_source']) ?? null,
  customer_reason: (signal.customer_reason as string) ?? null,
  customer_matched_domain: (signal.customer_matched_domain as string) ?? null,
});

export const getGtmSignalPersonaCounts = async (
  vendorId: number,
  filters?: {
    assignedTo?: string;
    todayViewAsUserId?: string;
    fitLabels?: string[];
    timeRange?: TaskTimeRange;
    includeCustomers?: boolean;
    /** Must match the list's own mode, or every tab badge overcounts (mb-server#6451). */
    customerFilter?: CustomerFilterMode;
    replyChannel?: 'email' | 'linkedin';
    /** The list's search, so each badge counts what its tab would show (mb-server#6130). */
    search?: string;
  },
): Promise<PersonaCountsResponse> => {
  const params: TaskQueryParams = { vendorId };
  if (filters?.assignedTo) params.assignedTo = filters.assignedTo;
  if (filters?.todayViewAsUserId) params.todayViewAsUserId = filters.todayViewAsUserId;
  if (filters?.fitLabels && filters.fitLabels.length > 0) {
    params.fitLabels = filters.fitLabels.join(',');
  }
  applyTaskTimeRangeParams(params, filters?.timeRange);
  if (filters?.includeCustomers) params.includeCustomers = 'true';
  if (filters?.customerFilter && filters.customerFilter !== 'all') params.customerFilter = filters.customerFilter;
  if (filters?.search?.trim()) params.search = filters.search.trim();

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params,
    url: GTM_SIGNALS_ENDPOINTS.personaCounts,
  });
  return result.data;
};

/**
 * Get single GTM signal with personas
 */
export const getGtmSignalDetail = async (
  signalId: string
): Promise<GtmSignalDetailResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: GTM_SIGNALS_ENDPOINTS.detail(signalId),
  });

  return result.data;
};

// Tasks wording and icon per signal type: a view of the one registry, re-exported
// here because most Tasks code has always imported it from this service.
export { SIGNAL_TYPE_CONFIG } from '@/constants/signalTypeCatalog';

export type DismissalReason =
  | 'not_right_person'
  | 'bad_fit'
  | 'wrong_timing'
  | 'no_budget'
  | 'already_customer'
  | 'using_competitor'
  | 'is_competitor'
  | 'duplicate'
  | 'invalid_contact'
  | 'not_relevant'
  | 'synced_to_crm'
  | 'other';

export const DISMISSAL_REASONS: Record<DismissalReason, string> = {
  // Positive resolution: the lead is valid and now handled in the CRM (e.g. synced to
  // Salesforce, assigned to a rep). Clears the task without implying a bad fit and is NOT a
  // permanent-block reason — it never blocks the company or affects AI qualification.
  synced_to_crm: 'Synced to CRM / Completed',
  not_right_person: 'Not the right person',
  bad_fit: 'Company not a good fit',
  wrong_timing: 'Wrong timing',
  no_budget: 'Budget constraints',
  already_customer: 'Already a customer',
  using_competitor: 'Using competitor',
  is_competitor: 'This is a competitor',
  duplicate: 'Duplicate signal',
  invalid_contact: 'Invalid contact info',
  not_relevant: 'Not relevant',
  other: 'Other reason',
};

/**
 * Company-level dismissal reasons that permanently block future signals.
 * These indicate the company will never be a fit (not timing/person-specific).
 * Backend adds company to ignored_customers table when dismissed with these reasons.
 */
export const PERMANENT_BLOCK_REASONS: readonly DismissalReason[] = [
  'bad_fit',
  'is_competitor',
  'already_customer',
] as const;

/**
 * Dismiss a GTM signal with reason
 */
export async function dismissGtmSignal(
  signalId: string,
  reason: DismissalReason,
  notes?: string
): Promise<{ success: boolean; signal: Partial<GtmSignal> }> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.dismiss(signalId),
    method: REQUEST_METHOD.PATCH,
    data: { reason, notes },
  });
  return response.data;
}

/**
 * Restore a dismissed GTM signal (undismiss)
 */
export async function restoreGtmSignal(
  signalId: string
): Promise<{ success: boolean; signal: Partial<GtmSignal> }> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.restore(signalId),
    method: REQUEST_METHOD.PATCH,
  });
  return response.data;
}

/**
 * GH#760: Dismiss a single persona (person-level dismissal)
 */
export async function dismissGtmPersona(
  personaId: string,
  reason: DismissalReason,
  notes?: string
): Promise<{ success: boolean; persona: Record<string, unknown>; signal_auto_dismissed: boolean }> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.personaDismiss(personaId),
    method: REQUEST_METHOD.PATCH,
    data: { reason, notes },
  });
  return response.data;
}

/**
 * #2136: Server-side bulk-dismiss limit per request. Mirrors the backend
 * BULK_DISMISS_LIMIT; cross-page select-all dismisses in batches of this size.
 */
export const BULK_DISMISS_PERSONA_LIMIT = 200;

export interface BulkDismissPersonasResult {
  success: boolean;
  /** Personas newly dismissed by this call. */
  dismissed: number;
  /** Requested ids that were not dismissed (not found / wrong vendor / already dismissed). */
  skipped: number;
  errors: number;
  /** Parent signals auto-dismissed because all their personas are now dismissed. */
  signals_auto_dismissed: number;
}

/**
 * #2136: Bulk-dismiss personas in one transactional request.
 *
 * Person-level reasons only — the backend rejects PERMANENT_BLOCK_REASONS with
 * a 400 (those are company-level and must use the signal-level dismiss path).
 * Callers must filter the reason picker to non-permanent reasons.
 */
export async function bulkDismissGtmPersonas(
  personaIds: string[],
  reason: DismissalReason,
  notes?: string
): Promise<BulkDismissPersonasResult> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.personaBulkDismiss,
    method: REQUEST_METHOD.POST,
    data: { personaIds, reason, notes },
  });
  return response.data;
}

/**
 * GH#760: Restore a dismissed persona (person-level restore)
 */
export async function restoreGtmPersona(
  personaId: string
): Promise<{ success: boolean; persona: Record<string, unknown>; signal_auto_restored: boolean }> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.personaRestore(personaId),
    method: REQUEST_METHOD.PATCH,
  });
  return response.data;
}

/**
 * Get workspace users (for assignee dropdowns)
 */
export async function getWorkspaceUsers(
  workspaceId: number
): Promise<{ success: boolean; users: WorkspaceUser[] }> {
  const response = await apiInstance({
    url: WORKSPACE_ENDPOINTS.users(workspaceId),
    method: REQUEST_METHOD.GET,
  });
  return response.data;
}

/**
 * Assign a GTM signal to a user
 */
export async function assignGtmSignal(
  signalId: string,
  assignedTo: string | null
): Promise<{ success: boolean; signal: Partial<GtmSignal> }> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.assign(signalId),
    method: REQUEST_METHOD.PATCH,
    data: { assignedTo },
  });
  return response.data;
}

/** Who a whole account's signals belong to. */
export interface AssignAccountResult {
  success: boolean;
  message: string;
  signalsUpdated: number;
  assignedTo: string | null;
  /** The signals the assign moved, so existing drafts can be rewritten under the new owner. */
  signalIds?: string[];
}

/**
 * Reassign every signal on an account, or on one person on it.
 *
 * Not `assignGtmSignal`: that moves a single signal, and a hub row is N of them.
 */
export async function assignSignalAccount(input: {
  companyDomain: string;
  personKey?: string | null;
  assignedTo: string | null;
}): Promise<AssignAccountResult> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.assignAccount,
    method: REQUEST_METHOD.PATCH,
    data: input,
  });
  return response.data;
}

/** One provider's answer after an on-demand lookup. */
export interface AccountCrmProviderResult {
  /** False means the provider could not be asked — NOT that the account is absent. */
  checked: boolean;
  /**
   * The provider's answer, present whenever it was reachable — INCLUDING a verified absence,
   * which arrives as `{ company_exists: false, last_checked }`. So `data != null` means "we
   * asked", never "we found it"; read the `*_exists` flag for that. Reading the truthiness of
   * `data` is what made "Check now" report "Found in HubSpot" for an account HubSpot had never
   * heard of (upcloud.com, 17 Sep).
   */
  data: {
    company_exists?: boolean;
    account_exists?: boolean;
    last_checked?: string;
    [key: string]: unknown;
  } | null;
}

export interface RefreshAccountCrmResult {
  success: boolean;
  companyDomain: string;
  signalsUpdated: number;
  hubspot: AccountCrmProviderResult;
  salesforce: AccountCrmProviderResult;
  /**
   * The named person's own contact records, under the same contract as the account halves.
   * `checked: false` with no data when the caller named nobody — never a claim of absence.
   * Optional because a browser can outlive a deploy: a tab loaded before the backend shipped
   * this will get a response without them, and the panel must keep whatever it was showing.
   */
  hubspotContact?: AccountCrmProviderResult;
  salesforceContact?: AccountCrmProviderResult;
}

/**
 * Look an account up in the connected CRMs now, and the person on the row with it.
 *
 * Account-scoped rather than signal-scoped because the rows that read
 * "Not checked yet" are precisely the ones with no signal id to refresh through —
 * and `contactEmail` is here for the same reason. The person's `contact_exists`
 * lives on the persona blob, which only the signal DETAIL payload carries, and
 * that request cannot be made without a signal id. So on a website-visit or
 * identified-visitor row a successful "Add to HubSpot" was held in local state
 * and lost on the next refresh (mb-server#6580).
 */
export async function refreshAccountCrm(
  companyDomain: string,
  contactEmail?: string | null,
): Promise<RefreshAccountCrmResult> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.refreshAccountCrm,
    method: REQUEST_METHOD.POST,
    data: contactEmail ? { companyDomain, contactEmail } : { companyDomain },
  });
  return response.data;
}

/**
 * Manually refresh CRM lookup data for a signal
 * Triggers immediate lookup in HubSpot and Salesforce for the company and all personas
 */
async function refreshSignalCrmData(
  signalId: string
): Promise<{
  success: boolean;
  message: string;
  data: {
    signal_updated: boolean;
    personas_updated: number;
    company_crm_data: CrmLookupData;
  };
}> {
  const response = await apiInstance({
    url: `/private/analytics/gtm-signals/${signalId}/refresh-crm-data`,
    method: REQUEST_METHOD.POST,
  });
  return response.data;
}

/**
 * Update a persona's email address
 * Resets validation status to allow re-validation
 */
export async function updatePersonaEmail(
  personaId: string,
  email: string | null
): Promise<{
  success: boolean;
  message: string;
  persona: {
    id: string;
    signal_id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    title: string | null;
    linkedin_url: string | null;
    email_validation_status: string | null;
    email_finding_status: string | null;
    email_verified: boolean;
    updated_at: string;
  };
}> {
  const response = await apiInstance({
    url: `/private/analytics/gtm-signals/personas/${personaId}`,
    method: REQUEST_METHOD.PATCH,
    data: { email },
  });
  return response.data;
}

export interface RestartBouncedSequenceResponse {
  success: true;
  message: string;
  workflowRunId: string;
  updatedPersona: {
    locked_workflow_id: number;
    locked_workflow_name: string | null;
    workflow_status: 'in_progress';
    bounced_email: null;
  };
}

/** Restart the current bounced runtime from Email 1 after the address changes. */
export async function restartBouncedSequence(
  personaId: string,
): Promise<RestartBouncedSequenceResponse> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.restartBouncedSequence(personaId),
    method: REQUEST_METHOD.POST,
  });
  return response.data;
}

/**
 * Update a persona's phone number
 */
export async function updatePersonaPhone(
  personaId: string,
  phone: string | null
): Promise<{
  success: boolean;
  message: string;
  persona: {
    id: string;
    signal_id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    title: string | null;
    linkedin_url: string | null;
    updated_at: string;
  };
}> {
  const response = await apiInstance({
    url: `/private/analytics/gtm-signals/personas/${personaId}/phone`,
    method: REQUEST_METHOD.PATCH,
    data: { phone },
  });
  return response.data;
}

/**
 * Update a persona's LinkedIn URL
 */
export async function updatePersonaLinkedin(
  personaId: string,
  linkedin_url: string | null
): Promise<{
  success: boolean;
  message: string;
  persona: {
    id: string;
    signal_id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    title: string | null;
    linkedin_url: string | null;
    updated_at: string;
  };
}> {
  const response = await apiInstance({
    url: `/private/analytics/gtm-signals/personas/${personaId}/linkedin`,
    method: REQUEST_METHOD.PATCH,
    data: { linkedin_url },
  });
  return response.data;
}

// Persona execution history (for follow-up signals)
export interface PersonaExecution {
  execution_id: number | string;
  executed_at: string;
  execution_started_at: string;
  status: string;
  action_type: string;
  /**
   * What kind of step this was, from its node's custom_action_details: the builder's `steps`, a
   * template's `step_type`, and `mode` ('none' for a step that writes nothing). Null for a direct
   * call; absent from servers older than the field.
   */
  step_details?: { steps?: unknown; step_type?: string | null; mode?: string | null } | null;
  play_name: string;
  audience_id: number | null;
  audience_name: string | null;
  workflow_triggered_at: string;
  total_calls: number;
  connected_calls: number;
  call_sentiment: string | null;
  call_status: string | null;
  is_voicemail: boolean;
  recording_url: string | null;
  duration_seconds: number | null;
  // AI analysis fields (direct calls)
  call_score: number | null;
  transcript: string | null;
  call_summary: string | null;
}

export async function getPersonaExecutionHistory(
  personaId: string,
): Promise<{ executions: PersonaExecution[]; count: number }> {
  const response = await apiInstance({
    url: `/private/analytics/gtm-signals/persona/${personaId}/executions`,
    method: REQUEST_METHOD.GET,
  });
  return response.data;
}

/** A contact an SDR supplies for an account persona discovery could not crack. */
export interface AddSignalContactBody {
  firstName?: string;
  lastName?: string;
  title?: string;
  email?: string;
  linkedinUrl?: string;
}

/**
 * Attach a manually-found contact to a `needs_contact` account.
 *
 * On success the account leaves the Needs Contact lane and appears in the
 * regular task lanes, because those are persona-centric.
 */
export const addSignalContact = async (
  signalId: string,
  body: AddSignalContactBody,
): Promise<{ success: true; persona: { id: number } }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: GTM_SIGNALS_ENDPOINTS.addContact(signalId),
    data: body,
  });
  return result.data as { success: true; persona: { id: number } };
};

/**
 * Board 4's per-row "Add email or LinkedIn" (mb-frontend#4638 phase 2): fills in contact
 * details on one persona the direct path already skipped for lack of a contact. Informational
 * only — this does not enroll anyone or change the persona's outcome; a rep runs Add to
 * outreach again from the signal to actually enroll them.
 */
/**
 * Board 5c: adding the contact enrolls the person the same way the intent enrolled everyone
 * else. `enrolling` is whether they are now queued for enrollment; `enrollment_reopened` whether
 * a settled intent was reopened for them.
 */
export interface AddPersonaContactResult {
  success: true;
  persona: { id: string; email: string | null; linkedin_url: string | null };
  enrollment_reopened?: boolean;
  enrolling?: boolean;
  /** Why the contact was saved without enrolling them; null when they are being enrolled. */
  reason?: 'needs_email' | 'not_sender' | 'no_mailbox' | 'ended' | 'newer_attempt' | null;
}

export const addPersonaContact = async (
  signalId: string,
  personaId: string,
  body: { email?: string; linkedinUrl?: string },
): Promise<AddPersonaContactResult> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: GTM_SIGNALS_ENDPOINTS.addPersonaContact(signalId, personaId),
    data: body,
  });
  return result.data as AddPersonaContactResult;
};


/** Account-level Today review queue item (mb-frontend#4459). */
export interface TodayReviewQueueItem {
  signal_id: string;
  company_name: string | null;
  company_domain: string | null;
  signal_type: string;
  fit_label: string | null;
  fit_score: number | null;
  reasoning: string | null;
  priority_score: number | null;
  priority: 'High' | 'Medium' | string;
  last_qualifying_activity_at: string | null;
  age_days: number | null;
  role_hints: string[];
  evidence: string;
  rank: number;
  assigned_to: string | null;
  /** Account-context meta line (Sunder ZIP 14 Sep): "{HQ city, country} · {industry} · {employee band}".
   * Each is independently nullable — the UI omits missing segments rather than showing a placeholder. */
  hq_location: string | null;
  industry: string | null;
  employee_band: string | null;
  /** The one-tap outreach intent for this signal, compact (mb-frontend#4638). */
  enrollment?: import('@/types/generated/signalEnrollment').EnrollmentRowSummary | null;
}

export interface TodayReviewQueueResponse {
  success: boolean;
  data: {
    items: TodayReviewQueueItem[];
    limit: number;
    expiry_days: number;
  };
}

export async function getTodayReviewQueue(params: {
  assignedTo?: string;
  limit?: number;
}): Promise<TodayReviewQueueResponse> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.reviewQueue,
    method: REQUEST_METHOD.GET,
    params: {
      assignedTo: params.assignedTo || 'me',
      limit: params.limit ?? 10,
    },
  });
  return response.data;
}

/** Rows per kind the Today Dismiss endpoint takes in one call. */
export const TODAY_DISMISS_MAX_ROWS = 200;

/** Exactly what one Today Dismiss changed: what its Undo takes back. */
export interface TodayDismissReceipt {
  /** ignored_customers rows it created; a company already ignored is not among them. */
  ignoredCompanyIds: number[];
  /** Signals it dismissed; one already dismissed is not among them. */
  signalIds: string[];
  /** People it dismissed; one already dismissed is not among them. */
  personaIds: string[];
  /** The Dismiss's timestamp, which each of those rows carries; Undo leaves a row that changed since. */
  dismissedAt: string;
}

/**
 * Today's Dismiss, in one server transaction: account rows (`signalIds`) ignore the company
 * and dismiss the signal, person rows (`personaIds`) dismiss the person. Every row or none.
 */
export async function dismissTodayRows(rows: { signalIds?: string[]; personaIds?: string[] }): Promise<TodayDismissReceipt> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.todayDismiss,
    method: REQUEST_METHOD.POST,
    data: { signalIds: rows.signalIds ?? [], personaIds: rows.personaIds ?? [] },
  });
  return response.data.data;
}

/** Take back a Today Dismiss: only what its receipt names and is still as it left it. Safe to repeat. */
export async function undoTodayDismiss(receipt: TodayDismissReceipt): Promise<{ companies: number; signals: number; people: number }> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.todayDismissUndo,
    method: REQUEST_METHOD.POST,
    data: receipt,
  });
  return response.data.data;
}

export async function acceptSignalForOutreach(
  signalId: string,
  assignedTo?: string,
): Promise<{
  success: boolean;
  deduplicated?: boolean;
  signalId: string;
  status: 'preparing' | 'ready' | string;
  personaIds: string[];
  companyDomain?: string | null;
  companyName?: string | null;
  code?: string;
  message?: string;
}> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.acceptForOutreach(signalId),
    method: REQUEST_METHOD.POST,
    data: { assignedTo: assignedTo || 'me' },
  });
  return response.data;
}

export async function undoAcceptSignalForOutreach(
  signalId: string,
): Promise<{ success: boolean; signalId?: string; code?: string; message?: string }> {
  const response = await apiInstance({
    url: GTM_SIGNALS_ENDPOINTS.undoAccept(signalId),
    method: REQUEST_METHOD.POST,
  });
  return response.data;
}
